import { Autocomplete, TextField, TextFieldProps } from '@mui/material'
import React, { FC, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from '../../hooks/helper/useTranslation'
import { useDebounce } from '../../../common/hooks/helper/useDebounce'
import { BoxWithLabel } from '../BoxWithLabel'
import { useDoctorPatientTreatmentsData } from '../../../views/doctor/Treatments/hooks/useDoctorPatientTreatmentsData'
import { DoctorPatientTreatment } from '../../../views/doctor/Treatments/types/treatments.types'
import { useSelectedLanguage } from '../../hooks/useSelectedLanguage'

type Props = TextFieldProps & {
  name: string
  label?: string
  required?: boolean
  showOwnTreatments?: boolean
}

export const TreatmentSelector: FC<React.PropsWithChildren<Props>> = ({
  name,
  label,
  required,
  showOwnTreatments = true,
  ...props
}) => {
  const { t } = useTranslation()
  const selectedLanguage = useSelectedLanguage()

  const [inputValue, setInputValue] = useState('')
  const searchTerm = useDebounce(inputValue)
  const { control } = useFormContext()

  const { activeTreatments, loading } = useDoctorPatientTreatmentsData({
    filterInput: { searchTerm, isOwn: showOwnTreatments },
  })

  return (
    <BoxWithLabel label={label}>
      <Controller
        control={control}
        name={name}
        rules={{
          required,
        }}
        render={({ field }) => (
          <Autocomplete<DoctorPatientTreatment, true, true>
            {...field}
            loading={loading}
            options={activeTreatments}
            onChange={(_, data) => {
              field.onChange(data)
            }}
            onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(item) => `${item.title[selectedLanguage]}`}
            filterOptions={(x) => x}
            noOptionsText={t('common:noOptions')}
            renderInput={(params) => (
              <TextField
                {...params}
                {...props}
                placeholder={t('common:search')}
                variant="outlined"
                name={name}
              />
            )}
            renderOption={(renderProps, item) => (
              <li
                {...renderProps}
                key={renderProps.key}
              >{`${item.title[selectedLanguage]}`}</li>
            )}
          />
        )}
      />
    </BoxWithLabel>
  )
}
